import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useSettings } from "@/context/SettingsContext";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { HoneypotField, readHoneypot, submitErrorMessage } from "@/components/HoneypotField";
import { Calculator, Check, Phone, Clock, ShieldCheck, MapPin } from "lucide-react";

const formatPhone = (p = "") => {
  const d = p.replace(/\D/g, "");
  return d.length === 10 ? d.match(/.{2}/g).join(" ") : p;
};
const PROPERTY_TYPES = ["Maison", "Appartement", "Terrain", "Garage", "Local commercial", "Parking"];
const EMPTY_FORM = { property_type: "", last_name: "", first_name: "", email: "", phone: "", street_number: "", street_name: "", postal_code: "", city: "", comment: "", consent: false };

export const EstimationPage = () => {
  const { settings } = useSettings();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const website = readHoneypot(e);
    if (!form.property_type) return toast.error("Veuillez choisir le type de bien à estimer");
    if (!form.consent) return toast.error("Veuillez accepter les mentions d'utilisation");
    setSubmitting(true);
    try {
      await axios.post(`${API}/contacts`, {
        website,
        name: `${form.first_name} ${form.last_name}`.trim(),
        email: form.email,
        phone: form.phone,
        subject: "Demande d'estimation",
        message: [
          `Type de bien : ${form.property_type}`,
          `Adresse du bien : ${form.street_number} ${form.street_name}, ${form.postal_code} ${form.city}`,
          form.comment.trim() && `Commentaire : ${form.comment.trim()}`,
          "Mentions d'utilisation acceptées : oui",
        ].filter(Boolean).join("\n"),
      });
      setSent(true);
      setForm(EMPTY_FORM);
    } catch (error) {
      toast.error(submitErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="estimation-page">
      <Header />

      <section className="relative py-32 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <p className="font-cinzel text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Estimation gratuite</p>
          <h1 className="font-serif text-4xl md:text-6xl text-white mb-6" data-testid="estimation-title">Estimer mon bien</h1>
          <p className="text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Confiez-nous quelques informations sur votre bien : nous vous recontactons rapidement pour une
            estimation réaliste, sans engagement, au Loroux-Bottereau, dans le Vignoble nantais et la région nantaise.
          </p>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="font-serif text-2xl mb-6">Pourquoi nous confier votre estimation ?</h2>
                <ul className="space-y-5">
                  {[
                    { icon: Check, text: "Une estimation réaliste, basée sur le marché local" },
                    { icon: Clock, text: "Un retour rapide, sous 48h" },
                    { icon: ShieldCheck, text: "Sans engagement et en toute confidentialité" },
                    { icon: MapPin, text: "Une connaissance fine du Vignoble nantais et de la région nantaise" },
                  ].map(({ icon: Icon, text }) => (
                    <li key={text} className="flex items-start gap-3 text-gray-700">
                      <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-[#0A0A0A] text-[#D4AF37]"><Icon size={16} /></span>
                      <span className="leading-relaxed">{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#0A0A0A] text-white p-8">
                <p className="font-cinzel text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Vous préférez nous appeler ?</p>
                <a href={`tel:${settings.phone?.replace(/\s/g, "")}`} className="flex items-center gap-3 text-xl font-medium tracking-wide hover:text-[#D4AF37] transition-colors" data-testid="estimation-phone">
                  <Phone size={20} className="text-[#D4AF37]" /> {formatPhone(settings.phone)}
                </a>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-[#F9F9F9] p-8 md:p-12" data-testid="estimation-form">
                {sent ? (
                  <div className="text-center py-12" data-testid="estimation-success">
                    <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[#D4AF37] text-[#0A0A0A]"><Check size={32} /></div>
                    <h2 className="font-serif text-2xl mb-3">Demande envoyée</h2>
                    <p className="text-gray-600 mb-8">Merci ! Nous étudions votre demande et vous recontactons très rapidement pour votre estimation.</p>
                    <Button variant="outline" onClick={() => setSent(false)} data-testid="estimation-new-request">Nouvelle demande</Button>
                  </div>
                ) : (
                  <>
                    <h2 className="font-serif text-2xl mb-2 flex items-center gap-3"><Calculator size={22} className="text-[#D4AF37]" /> Demande d'estimation</h2>
                    <p className="text-gray-600 mb-8">Les champs marqués * sont obligatoires.</p>
                    <form onSubmit={handleSubmit} className="space-y-6" style={{ position: "relative" }}>
                      <HoneypotField />
                      <div className="space-y-2">
                        <Label>Type de bien à estimer *</Label>
                        <Select value={form.property_type || undefined} onValueChange={(v) => set("property_type", v)}>
                          <SelectTrigger data-testid="estimation-form-type"><SelectValue placeholder="Sélectionnez le type de bien" /></SelectTrigger>
                          <SelectContent>
                            {PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="e-last">Nom *</Label>
                          <Input id="e-last" required value={form.last_name} placeholder="Votre nom" onChange={(e) => set("last_name", e.target.value)} data-testid="estimation-form-last-name" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="e-first">Prénom *</Label>
                          <Input id="e-first" required value={form.first_name} placeholder="Votre prénom" onChange={(e) => set("first_name", e.target.value)} data-testid="estimation-form-first-name" />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="e-email">Adresse e-mail *</Label>
                          <Input id="e-email" type="email" required value={form.email} placeholder="votre@email.com" onChange={(e) => set("email", e.target.value)} data-testid="estimation-form-email" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="e-phone">Téléphone *</Label>
                          <Input id="e-phone" type="tel" required value={form.phone} placeholder="06 00 00 00 00" onChange={(e) => set("phone", e.target.value)} data-testid="estimation-form-phone" />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Adresse postale complète du bien à estimer *</Label>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div className="space-y-2 md:col-span-1">
                            <Label htmlFor="e-number" className="text-xs text-gray-500">N° de rue</Label>
                            <Input id="e-number" required value={form.street_number} placeholder="65" onChange={(e) => set("street_number", e.target.value)} data-testid="estimation-form-street-number" />
                          </div>
                          <div className="space-y-2 md:col-span-3">
                            <Label htmlFor="e-street" className="text-xs text-gray-500">Nom de la voie</Label>
                            <Input id="e-street" required value={form.street_name} placeholder="impasse des Tonneliers" onChange={(e) => set("street_name", e.target.value)} data-testid="estimation-form-street-name" />
                          </div>
                          <div className="space-y-2 md:col-span-1">
                            <Label htmlFor="e-zip" className="text-xs text-gray-500">Code postal</Label>
                            <Input id="e-zip" required inputMode="numeric" pattern="[0-9]{5}" maxLength={5} value={form.postal_code} placeholder="44230" onChange={(e) => set("postal_code", e.target.value.replace(/\D/g, ""))} data-testid="estimation-form-postal-code" />
                          </div>
                          <div className="space-y-2 md:col-span-3">
                            <Label htmlFor="e-city" className="text-xs text-gray-500">Commune</Label>
                            <Input id="e-city" required value={form.city} placeholder="Le Loroux-Bottereau" onChange={(e) => set("city", e.target.value)} data-testid="estimation-form-city" />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="e-comment">Commentaire <span className="text-gray-400 font-normal">(optionnel)</span></Label>
                        <Textarea id="e-comment" rows={4} value={form.comment} placeholder="Surface, nombre de pièces, travaux récents, délai envisagé…" onChange={(e) => set("comment", e.target.value)} data-testid="estimation-form-comment" />
                      </div>

                      <div className="flex items-start gap-3">
                        <Checkbox id="e-consent" checked={form.consent} onCheckedChange={(v) => set("consent", v === true)} className="mt-0.5" data-testid="estimation-form-consent" />
                        <Label htmlFor="e-consent" className="text-sm text-gray-700 leading-relaxed cursor-pointer font-normal">
                          J'ai lu et j'accepte les <Link to="/politique-confidentialite" target="_blank" className="underline hover:text-[#D4AF37]" data-testid="estimation-form-consent-link">mentions d'utilisation</Link> *
                        </Label>
                      </div>

                      <Button type="submit" disabled={submitting} className="w-full md:w-auto bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-12" data-testid="estimation-form-submit">
                        {submitting ? "Envoi en cours..." : (<><Calculator size={18} className="mr-2" /> Estimer</>)}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EstimationPage;
